import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { LinkedItemSearchMenu } from 'shared/components/ui/linked-item-search-menu';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes';
import { InteractionType } from 'shared/types/data-type';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { LinkOptions } from 'shared/types/task';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './task-meta-interaction-linked-item.style';

export type TaskMetaInteractionGoalProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  value?: ID | null;
  onChange: (link: { id: ID; type: LinkOptions } | null) => void;
  onCreate?: (name: string) => void;
  showGoalColor?: boolean;
  hasIcon?: boolean;
  highlight?: boolean;
  isCreate?: boolean;
};

export const TaskMetaInteractionLinkedItem: React.FC<
  TaskMetaInteractionGoalProps
> = ({
  goals,
  lifeAreas,
  value,
  onChange,
  onCreate,
  hasIcon,
  highlight,
  isCreate,
  ...rest
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const { item, itemIsGoal: isGoalItem } = useMemo(() => {
    const goalItem = goals.find(({ id }) => id === value);
    if (goalItem) {
      return { item: goalItem, itemIsGoal: true };
    }
    const lifeAreaItem = lifeAreas.find(({ id }) => id === value);
    return { item: lifeAreaItem, itemIsGoal: false };
  }, [goals, lifeAreas, value]);

  const color = useMemo(
    () =>
      item?.id
        ? isGoalItem
          ? getLinkedItemColor(item.id, { lifeAreas, goals }) ?? undefined
          : item.color
        : undefined,
    [item?.id, item?.color, isGoalItem, lifeAreas, goals],
  );

  const onCreateGoal = (name: string) => {
    onCreate?.(name);
    closeMenu();
  };

  const _onChange = (link: { id: ID; type: LinkOptions } | null) => {
    onChange(link);
    closeMenu();
  };

  const navigateToItem = () => {
    if (!item) {
      return;
    }

    navigate(
      replaceUrlParams(isGoalItem ? Paths.GoalDetail : Paths.LifeAreaDetail, {
        id: item.id,
      }),
    );
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [menuOpen]);

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={
          color ? (
            <Styled.Color $color={color} />
          ) : hasIcon ? (
            <Icon icon={Target} />
          ) : undefined
        }
        color={highlight && item ? Colors.TextPrimary : Colors.Inherit}
        onClick={item && !isCreate ? navigateToItem : openMenu}
        label={item?.name}
        selected={menuOpen}
        tooltipLabel={t(
          item
            ? 'task.linked-item.tooltip.edit.label'
            : 'task.linked-item.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <LinkedItemSearchMenu
          goals={goals}
          lifeAreas={lifeAreas}
          onChange={_onChange}
          onCreate={onCreate ? onCreateGoal : undefined}
          value={value}
          referenceElement={containerRef}
          onOutside={closeMenu}
          interactionType={InteractionType.Task}
        />
      )}
    </Styled.Container>
  );
};
